import { Component } from "react";

/**
 * Global ErrorBoundary — wraps the entire app.
 * Prevents the "black screen of death" when an uncaught React error occurs.
 * Shows a recovery UI instead of the default blank/black background.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    // Log for debugging — visible in browser console + any error monitoring
    console.error("[ErrorBoundary] Uncaught error:", error);
    console.error("[ErrorBoundary] Component stack:", errorInfo?.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#0A0A0C",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            width: "100%",
            background: "linear-gradient(160deg, #16161E 0%, #0E0E14 100%)",
            border: "1px solid rgba(212,175,55,0.2)",
            borderRadius: 20,
            padding: "36px 28px",
            textAlign: "center",
            boxShadow: "0 24px 60px -8px rgba(0,0,0,0.6)",
          }}
        >
          {/* Gold top line */}
          <div style={{ height: 2, background: "linear-gradient(90deg, transparent, #D4AF37, transparent)", borderRadius: 2, marginBottom: 28 }} />

          {/* Icon */}
          <div style={{ fontSize: 36, marginBottom: 16 }}>⚠</div>

          <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 800, marginBottom: 8, letterSpacing: "-0.3px" }}>
            Algo salió mal
          </h2>
          <p style={{ color: "#71717A", fontSize: 13, lineHeight: 1.6, marginBottom: 28 }}>
            La aplicación encontró un error inesperado. Tus datos están seguros.
            Intenta recargar la página.
          </p>

          {/* Error detail — always visible to help debug production issues */}
          {this.state.error && (
            <pre style={{
              color: "#EF4444",
              fontSize: 11,
              background: "rgba(239,68,68,0.06)",
              border: "1px solid rgba(239,68,68,0.15)",
              borderRadius: 8,
              padding: 12,
              overflow: "auto",
              maxHeight: 120,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              marginBottom: 24,
              textAlign: "left",
            }}>
              {this.state.error.toString()}
              {this.state.errorInfo?.componentStack
                ? "\n" + this.state.errorInfo.componentStack.split("\n").slice(1, 4).join("\n")
                : ""}
            </pre>
          )}

          <div style={{ display: "flex", gap: 10 }}>
            <button
              onClick={this.handleGoHome}
              data-testid="error-boundary-home"
              style={{
                flex: 1,
                padding: "12px 0",
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "transparent",
                color: "#A1A1AA",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Ir al inicio
            </button>
            <button
              onClick={this.handleReload}
              data-testid="error-boundary-reload"
              style={{
                flex: 1,
                padding: "12px 0",
                borderRadius: 12,
                border: "none",
                background: "linear-gradient(135deg, #D4AF37 0%, #B8941F 100%)",
                color: "#0A0A0C",
                fontSize: 13,
                fontWeight: 800,
                cursor: "pointer",
                boxShadow: "0 4px 14px -3px rgba(212,175,55,0.4)",
              }}
            >
              Recargar página
            </button>
          </div>
        </div>
      </div>
    );
  }
}
